import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import MomentumScore from '../components/MomentumScore';
import DailyChecklist from '../components/DailyChecklist';
import HabitForm from '../components/HabitForm';
import ConfettiCelebration from '../components/ConfettiCelebration';
import StreakBreakModal from '../components/StreakBreakModal';
import StreakAtRiskModal from '../components/StreakAtRiskModal';
import PremiumPreviewBanner from '../components/PremiumPreviewBanner';
import { SkeletonDashboard } from '../components/SkeletonCard';
import Logo from '../components/Logo';
import useStore from '../store';
import { useActiveUser } from '../hooks/useActiveUser';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { metaRepo } from '../services/db/meta';
import { FREE_HABIT_LIMIT } from '../utils/constants';
import { getToday } from '../utils/dates';
import {
  summarizeHabit,
  momentumScore as calcMomentum,
  groupCheckinsByHabit,
  streakAtRiskHabits,
} from '../utils/streakCalc';

export default function Dashboard() {
  const navigate = useNavigate();
  const { isAnonymous, isAuthed } = useActiveUser();
  const {
    habits,
    allCheckins,
    allFreezes,
    todayCheckins,
    setTodayCheckins,
    fetchHabits,
    createHabit,
    checkin,
    undoCheckin,
    showHabitForm,
    editingHabit,
    setShowHabitForm,
    showPremiumModal,
    setShowPremiumModal,
    subscription,
    fetchSubscription,
  } = useStore();

  const [milestone, setMilestone] = useState(null);
  const [brokenHabit, setBrokenHabit] = useState(null);
  const [streakAtRiskHabit, setStreakAtRiskHabit] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  // Once the user dismisses a nudge for a habit, don't resurrect it this session
  // when unrelated state changes re-run the detection effects.
  const dismissedBrokenRef = useRef(new Set());
  const dismissedAtRiskRef = useRef(new Set());

  useEscapeKey(() => setShowPremiumModal(false), showPremiumModal);

  const isPremium = useStore((s) => s.isPremium);

  // Subscribe to firstSyncComplete so this effect re-runs after the
  // initial server pull lands. Authed users with empty local Dexie
  // must NOT be sent to Onboarding before that pull resolves —
  // otherwise a fresh-install sign-in dumps the user into the wizard
  // even though the server has all their habits.
  const firstSyncComplete = useStore((s) => s.firstSyncComplete);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      await fetchHabits();
      await fetchSubscription();
      if (cancelled) return;
      const habitsNow = useStore.getState().habits;
      if (habitsNow.length > 0) {
        setInitialLoad(false);
        return;
      }
      const onboardedAt = await metaRepo.getOnboardedAt();
      if (onboardedAt) {
        setInitialLoad(false);
        return;
      }
      // Empty state. For authed users, wait for the first sync to
      // finish before deciding to redirect — the pull may be about
      // to deliver their habits. Anonymous users have nothing to
      // wait for, so redirect immediately.
      if (isAuthed && !firstSyncComplete) return;
      if (!cancelled) navigate('/onboarding', { replace: true });
    }
    load();
    return () => { cancelled = true; };
  }, [fetchHabits, fetchSubscription, navigate, isAuthed, firstSyncComplete]);

  // Derive everything from local state.
  const checkinsByHabit = useMemo(() => groupCheckinsByHabit(allCheckins), [allCheckins]);
  const freezesByHabit = useMemo(() => groupCheckinsByHabit(allFreezes), [allFreezes]);

  const habitsWithStats = useMemo(() => {
    return habits.map((h) => {
      const dates = checkinsByHabit[h.habitId] ?? [];
      const freezes = freezesByHabit[h.habitId] ?? [];
      const summary = summarizeHabit(h, dates, new Date(), freezes);
      return { ...h, ...summary };
    });
  }, [habits, checkinsByHabit, freezesByHabit]);

  const momentumScore = useMemo(
    () => calcMomentum(habits, checkinsByHabit, new Date(), freezesByHabit),
    [habits, checkinsByHabit, freezesByHabit],
  );

  // Build today's checkin map from local data.
  useEffect(() => {
    const today = getToday();
    const map = {};
    for (const h of habits) {
      map[h.habitId] = (checkinsByHabit[h.habitId] ?? []).includes(today);
    }
    setTodayCheckins(map);
  }, [habits, checkinsByHabit, setTodayCheckins]);

  // Broken streak detection: had a >= 3 best streak, current is 0, not done today.
  useEffect(() => {
    if (initialLoad) return;
    if (brokenHabit) return; // one at a time; don't re-target while already shown
    const today = getToday();
    for (const h of habitsWithStats) {
      if (dismissedBrokenRef.current.has(h.habitId)) continue;
      if (h.currentStreak === 0 && h.bestStreak >= 3 && !(checkinsByHabit[h.habitId] ?? []).includes(today)) {
        setBrokenHabit(h);
        break;
      }
    }
  }, [habitsWithStats, checkinsByHabit, initialLoad, brokenHabit]);

  // Streak at risk: free users, 7+ day current streak, target day, not yet done.
  useEffect(() => {
    if (initialLoad) return;
    if (isPremium()) return;
    if (streakAtRiskHabit) return;
    const atRisk = streakAtRiskHabits(habits, checkinsByHabit, new Date(), freezesByHabit)
      .filter((h) => !dismissedAtRiskRef.current.has(h.habitId));
    if (atRisk.length > 0) setStreakAtRiskHabit(atRisk[0]);
  }, [habits, checkinsByHabit, freezesByHabit, initialLoad, isPremium, streakAtRiskHabit]);

  const handleCheckin = useCallback(async (habitId) => {
    try {
      const result = await checkin(habitId);
      if (result?.milestone) {
        setMilestone(result.milestone);
        setTimeout(() => setMilestone(null), 4000);
      }
    } catch {
      toast.error('Failed to check in');
    }
  }, [checkin]);

  const handleUndo = useCallback(async (habitId) => {
    try {
      await undoCheckin(habitId);
    } catch {
      toast.error('Failed to undo');
    }
  }, [undoCheckin]);

  const handleCreateHabit = useCallback(async (habitData) => {
    try {
      await createHabit(habitData);
      toast.success('Habit created!');
    } catch (err) {
      if (err.code === 'HABIT_LIMIT') {
        setShowPremiumModal(true);
      } else {
        toast.error('Failed to create habit');
      }
      throw err;
    }
  }, [createHabit, setShowPremiumModal]);

  function handleAddHabit() {
    if (!isPremium() && habits.length >= FREE_HABIT_LIMIT) {
      setShowPremiumModal(true);
      return;
    }
    setShowHabitForm(true);
  }

  const totalCheckins = habitsWithStats.reduce((sum, h) => sum + (h.totalCompletions || 0), 0);
  const bestStreak = habitsWithStats.reduce((max, h) => Math.max(max, h.bestStreak || 0), 0);

  // initialLoad covers both Dexie-fetch-in-flight and the wait for
  // the first server sync to resolve before the redirect decision
  // is made.
  if (initialLoad) {
    return <SkeletonDashboard />;
  }

  function handleUpgradeClick() {
    if (isAnonymous) {
      navigate('/login?intent=upgrade');
      return;
    }
    navigate('/premium?source=habit_limit');
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between shadow-sm safe-area-top">
        <div className="flex items-center gap-2">
          <Logo size={28} rounded="rounded-lg" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Streaks</h1>
        </div>
        <div className="flex items-center gap-1">
          {!isAuthed && (
            <button
              onClick={() => navigate('/login')}
              className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              Sign in
            </button>
          )}
          {isAuthed && (
            <button
              onClick={() => navigate('/settings')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              aria-label="Settings"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-4 max-w-lg mx-auto space-y-4">
        <MomentumScore score={momentumScore} />

        {subscription?.premiumPreview?.active && (
          <PremiumPreviewBanner premiumPreview={subscription.premiumPreview} />
        )}

        <DailyChecklist
          habits={habitsWithStats}
          todayCheckins={todayCheckins}
          onCheckin={handleCheckin}
          onUndo={handleUndo}
        />

        <button
          onClick={handleAddHabit}
          className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50 dark:hover:border-primary-500 dark:hover:text-primary-400 dark:hover:bg-primary-900/10 transition-all font-medium"
        >
          + Add Habit {!isPremium() && habits.length >= FREE_HABIT_LIMIT && '(Premium)'}
        </button>
      </div>

      {showHabitForm && (
        <HabitForm
          habit={editingHabit}
          onSave={handleCreateHabit}
          onClose={() => setShowHabitForm(false)}
        />
      )}

      {showPremiumModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPremiumModal(false)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl p-6 text-center animate-scale-in">
            <div className="text-4xl mb-3">✨</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {isAnonymous ? 'Sign in to unlock more' : 'Upgrade to Premium'}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {isAnonymous
                ? `Free plan is limited to ${FREE_HABIT_LIMIT} habits. Sign in to back up your progress and upgrade.`
                : `You've reached the free limit of ${FREE_HABIT_LIMIT} habits.`}
            </p>
            {(totalCheckins > 0 || bestStreak > 0) && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  You've built <strong>{totalCheckins} check-in{totalCheckins !== 1 ? 's' : ''}</strong>
                  {bestStreak > 0 && (
                    <> and a <strong>{bestStreak}-day best streak</strong></>
                  )}
                  . Keep the momentum going{isAnonymous ? ' — sign in to keep it safe.' : ' with Premium.'}
                </p>
              </div>
            )}
            <button
              onClick={() => {
                setShowPremiumModal(false);
                handleUpgradeClick();
              }}
              className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors mb-2"
            >
              {isAnonymous ? 'Sign in' : 'View Plans'}
            </button>
            <button
              onClick={() => setShowPremiumModal(false)}
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Show at most one nudge at a time — the paywall modal takes precedence,
          then at-risk, then broken-streak. */}
      {!showPremiumModal && streakAtRiskHabit && !isPremium() && (
        <StreakAtRiskModal
          habit={streakAtRiskHabit}
          onClose={() => {
            dismissedAtRiskRef.current.add(streakAtRiskHabit.habitId);
            setStreakAtRiskHabit(null);
          }}
          onCheckin={() => handleCheckin(streakAtRiskHabit.habitId)}
        />
      )}

      {!showPremiumModal && !streakAtRiskHabit && brokenHabit && (
        <StreakBreakModal
          habit={brokenHabit}
          onClose={() => {
            dismissedBrokenRef.current.add(brokenHabit.habitId);
            setBrokenHabit(null);
          }}
          onStartFresh={() => handleCheckin(brokenHabit.habitId)}
        />
      )}

      <ConfettiCelebration milestone={milestone} />
    </div>
  );
}
